import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { authService } from '../../services/authService';
import { getApiUrl } from '../../config/env';
import Button from '../../components/UI/Button';
import {
  AuthCard,
  AuthCardHeader,
  AuthTrustFooter,
  AuthField,
  AuthInputWrapper,
  authInputWithIconClass,
  PasswordStrength,
  allPasswordRulesMet,
  AuthSuccessState,
  AuthPageFrame,
} from '../../components/Auth';


interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const friendlyResetError = (err: unknown): string => {
  const message = err instanceof Error ? err.message : 'Something went wrong';
  if (/500|status code 500|internal server error/i.test(message)) {
    return 'We could not reset your password right now. Please request a fresh reset link and try again.';
  }
  if (/404|not found|status code 404/i.test(message)) {
    return 'Password reset is not available on the server yet. Please contact support.';
  }
  if (/Network Error|Failed to fetch|ECONNREFUSED|ERR_NETWORK|fetch/i.test(message)) {
    const apiUrl = getApiUrl();
    return `Cannot reach the API at ${apiUrl}. Start your backend or set REACT_APP_API_URL in .env.`;
  }
  return message;
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token')?.trim() || '', [searchParams]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  const passwordValue = watch('password') ?? '';
  const confirmPassword = watch('confirmPassword') ?? '';
  const missingToken = !token;

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setFormError('This reset link is missing a token. Request a new link from forgot password.');
      return;
    }
    if (!allPasswordRulesMet(data.password)) {
      setFormError('Please meet all password requirements.');
      return;
    }

    try {
      setIsLoading(true);
      setFormError(null);
      await authService.resetPasswordWithToken(token, data.password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2800);
    } catch (err) {
      setFormError(friendlyResetError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageFrame>
      <AuthCard>
        <AuthCardHeader
          title="Set a new password"
          subtitle="Choose a strong password you haven't used elsewhere"
        />

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthSuccessState
                title="Password updated"
                description="Your password has been reset securely."
                loadingMessage="Redirecting you to sign in…"
              >
                <Link to="/login">
                  <Button type="button" variant="primary" size="lg" className="w-full rounded-xl">
                    Go to sign in
                  </Button>
                </Link>
              </AuthSuccessState>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {missingToken ? (
                <div
                  role="alert"
                  className="mb-6 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
                    <div>
                      <h2 className="text-sm font-semibold text-amber-900">Invalid or expired link</h2>
                      <p className="mt-1 text-sm text-amber-800">
                        This page needs a token in the URL. Request a new reset email to get a fresh
                        link.
                      </p>
                      <Link
                        to="/forgot-password"
                        className="mt-2 inline-block text-sm font-medium text-amber-900 underline hover:text-amber-950"
                      >
                        Forgot password
                      </Link>
                    </div>
                  </div>
                </div>
              ) : null}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <AuthField label="New password" htmlFor="reset-password" error={errors.password?.message}>
                  <AuthInputWrapper hasError={!!errors.password}>
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden /></div>
                    <input
                      id="reset-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      disabled={missingToken}
                      placeholder="Enter a new password"
                      className={`${authInputWithIconClass(!!errors.password)} pr-11`}
                      {...register('password', {
                        required: 'Password is required',
                        validate: (v) => allPasswordRulesMet(v) || 'Password does not meet requirements',
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center rounded-r-xl px-3 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={missingToken}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </AuthInputWrapper>
                  <PasswordStrength
                    password={passwordValue}
                    showMatch
                    matchValue={confirmPassword}
                    className="mt-3"
                    alwaysShowRules
                    showUnmetErrors={!!errors.password}
                  />
                </AuthField>

                <AuthField
                  label="Confirm new password"
                  htmlFor="reset-confirm-password"
                  error={errors.confirmPassword?.message}
                >
                  <AuthInputWrapper hasError={!!errors.confirmPassword}>
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-4 w-4 text-gray-400" aria-hidden /></div>
                    <input
                      id="reset-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      disabled={missingToken}
                      placeholder="Confirm your new password"
                      className={`${authInputWithIconClass(!!errors.confirmPassword)} pr-11`}
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (v) => v === passwordValue || 'Passwords do not match',
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center rounded-r-xl px-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={missingToken}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </AuthInputWrapper>
                </AuthField>

                {formError ? (
                  <motion.div
                    role="alert"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
                    <p className="text-sm text-red-500">{formError}</p>
                  </motion.div>
                ) : null}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isLoading}
                  disabled={missingToken}
                  className="w-full rounded-xl"
                >
                  Update password
                  {!isLoading ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden /> : null}
                </Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {!success ? (
          <p className="mt-6 text-center text-sm text-gray-600">
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
              Back to sign in
            </Link>
          </p>
        ) : null}

        <AuthTrustFooter />
      </AuthCard>
    </AuthPageFrame>
  );
};

export default ResetPassword;
